import React, { useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  List,
  ListItem,
  Divider,
  TextField,
  Paper,
  Stack,
  InputBase,
  Card,
  CardContent,
  Slide
} from '@mui/material';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from '../firebase-config';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { AppCTX } from '../data/AppContext';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function ClientDetails({open, selectedProspect, setSelectedProspect, setShowDetails}) {
  const { updateProspect, user } = React.useContext(AppCTX);
  const [owner1name, setowner1name] = useState('');
  const [owner1number, setowner1number] = useState('');
  const [owner2name, setowner2name] = useState('');
  const [owner2number, setowner2number] = useState('');
  const [utilityBill, setutilityBill] = useState(null);
  const [notes, setNotes] = useState([]);
  const [inputText, setInputText] = useState('');

  React.useEffect(() => {
    if (selectedProspect != null) {
      if (selectedProspect.utilityBill != null) {
        setutilityBill(selectedProspect.utilityBill);
      }
      if (selectedProspect.owners.length === 1) {
        setowner1name(selectedProspect.owners[0].name);
        setowner1number(selectedProspect.owners[0].number);
      } else if (selectedProspect.owners.length === 2) {
        setowner1name(selectedProspect.owners[0].name);
        setowner1number(selectedProspect.owners[0].number);
        setowner2name(selectedProspect.owners[1].name);
        setowner2number(selectedProspect.owners[1].number);
      }
      setNotes(selectedProspect.notes);
    } else {
      setowner1name('');
      setowner1number('');
      setutilityBill(null);
      setNotes([]);
      setInputText('');
    }
  }, [selectedProspect]);

  const handleNameChange = (e, id) => {
    if (id === 0) {
      setowner1name(e.target.value);
    } else {
      setowner2name(e.target.value);
    }
  };

  const handleNumberChange = (e, id) => {
    if (id === 0) {
      setowner1number(e.target.value);
    } else {
      setowner2number(e.target.value);
    }
  };

  const handleSave = async () => {
    try {
      let update = {...selectedProspect};
      if (update.owners.length === 1) {
        update.owners[0].name = owner1name;
        update.owners[0].number = owner1number;
      } else if (update.owners.length === 2) {
        update.owners[0].name = owner1name;
        update.owners[0].number = owner1number;
        update.owners[1].name = owner2name;
        update.owners[1].number = owner2number;
      }
      update.utilityBill = utilityBill;
      update.notes = notes;
      await updateProspect(update);
    } catch (err) {
      console.log('Error:', err);
    }
    setShowDetails(false);
    setSelectedProspect(null);
  };

  const handleClose = () => {
    setShowDetails(false);
    setSelectedProspect(null);
  };

  const addNote = () => {
    if (inputText.trim() !== '') {
      const update = [...notes];
      update.push(user.data.name + " - " + inputText);
      setInputText('');
      setNotes(update);
    }
  };

  const uploadUtilityBill = async (e) => {
    try {
      const electricityBillRef = ref(storage, 'electricity-bills/' + selectedProspect.address + '.jpg');
      let result = await uploadBytes(electricityBillRef, e.target.files[0]);
      let url = await getDownloadURL(ref(storage, result.ref.fullPath));
      setutilityBill(url);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      id="clientDetailsDialog"
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative', bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1, color:'white' }} variant="h6">
            Home Details
          </Typography>
          <Button color="inherit" onClick={handleSave}>
            Save Changes
          </Button>
        </Toolbar>
      </AppBar>

      <Paper className="listwrapper-paper" sx={{ height: "100%", bgcolor: '#f5f5f5' }} elevation={0}>
        <List sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
          {/* Owner Information Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Owner Information
              </Typography>
              <Stack spacing={3}>
                {selectedProspect?.owners.map((owner, idx) => (
                  <Paper key={owner.name + owner.number} elevation={0} sx={{ p: 2, bgcolor: '#fff' }}>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Owner {idx + 1}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label="Name"
                        variant="outlined"
                        value={idx === 0 ? owner1name : owner2name}
                        onChange={(e) => handleNameChange(e, idx)}
                      />
                      <TextField
                        fullWidth
                        label="Phone Number"
                        variant="outlined"
                        value={idx === 0 ? owner1number : owner2number}
                        onChange={(e) => handleNumberChange(e, idx)}
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* Address Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Property Address
              </Typography>
              <Typography variant="body1" sx={{ p: 2, bgcolor: '#fff', borderRadius: 1 }}>
                {selectedProspect?.address}
              </Typography>
            </CardContent>
          </Card>

          {/* Utility Bill Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Utility Bill
              </Typography>
              {utilityBill ? (
                <Paper elevation={2} sx={{ mt: 2, overflow: 'hidden', borderRadius: 2 }}>
                  <img
                    alt="utility bill"
                    src={utilityBill}
                    style={{ width: '100%', display: 'block' }}
                  />
                </Paper>
              ) : (
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mt: 2 }}
                >
                  Upload Utility Bill
                  <VisuallyHiddenInput onChange={uploadUtilityBill} type="file" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Notes Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Notes
              </Typography>
              <Stack spacing={2}>
                {notes.map((note, idx) => (
                  <Paper
                    key={idx}
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: '#fff',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body1">
                      {note}
                    </Typography>
                  </Paper>
                ))}
              </Stack>

              <Paper
                elevation={2}
                sx={{
                  p: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  mt: 3,
                  borderRadius: 2
                }}
              >
                <InputBase
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Add a note..."
                  sx={{ ml: 1, flex: 1 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addNote();
                    }
                  }}
                />
                <IconButton 
                  onClick={addNote}
                  color="primary"
                  sx={{ 
                    ml: 1,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    }
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Paper>
            </CardContent>
          </Card>
        </List>
      </Paper>
    </Dialog>
  );
}